/**
 * Declare more parts inside a definition that already exists.
 *
 * Patrick's second observation, and the reason the pane's "Add child part" button
 * was disabled until now: a decomposition is rarely known in full at the moment a
 * definition is made, and everything after that moment had no route. It has one -
 * `POST /api/parts/{id}/children` - and it takes the SAME rows `child_parts` takes
 * at creation, so this dialog is the wizard's child table with a different
 * destination and nothing else different.
 *
 * All or nothing, like creation: four rows where the third names an abstract
 * definition add none of them, and a failure at the commit leaves nothing behind.
 * So the refusal is rendered and the same rows can simply be sent again, rather
 * than this dialog trying to work out how far it got.
 *
 * An ABSTRACT owner is allowed to be given children, deliberately: `abstract`
 * constrains instantiation and not decomposition, and every specialization
 * inherits the decomposition. Only a CHILD may not be abstract, and the picker
 * cannot offer one because it is fed from `usable_as_type` - the server's answer,
 * not a filter written here.
 */

import { useEffect, useMemo, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import type { PartDetail, PartRow } from '../../schema'
import { useCanWrite } from '../../state/AppData'
import { useToasts } from '../../state/Toasts'
import { MONOSPACE } from '../../theme'
import ChildPartsTable from './ChildPartsTable'
import { addedSummary, childPartRows, childRowsRefusal, emptyChildDraft } from './parts-editing'
import type { ChildDraft } from './parts-editing'
import { typeChoices } from './part-wizard-model'

/** Enough rows to be the whole model; the listing is paged and this is a picker. */
const EVERYTHING = 2000

export default function AddChildDialog({
  part,
  onClose,
  onAdded,
}: {
  /** The definition the usages are declared inside. */
  part: PartDetail
  onClose: () => void
  /** Called once the children exist, so the pane can read them back. */
  onAdded: () => void
}) {
  const permission = useCanWrite()
  const toasts = useToasts()

  const [usable, setUsable] = useState<PartRow[]>([])
  const [rows, setRows] = useState<ChildDraft[]>(() => [emptyChildDraft('row-0')])
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')
  const [fieldError, setFieldError] = useState('')

  useEffect(() => {
    let live = true
    api
      .parts({ usable_as_type: true, limit: EVERYTHING })
      .then((page) => live && setUsable(page.items))
      .catch(() => live && setUsable([]))
    return () => {
      live = false
    }
  }, [])

  const options = useMemo(() => typeChoices(usable), [usable])
  const refusal = childRowsRefusal(rows, permission)

  const submit = async () => {
    setBusy(true)
    setFailure('')
    setFieldError('')
    try {
      const created = await api.addChildParts(part.id, childPartRows(rows))
      toasts.succeeded(addedSummary(created))
      onAdded()
      onClose()
    } catch (error) {
      const failed = error as ApiError
      // Both, and for the same reason the wizard shows both: the API's own
      // refusals - an abstract child, an owner that is a usage, a bad
      // multiplicity - arrive as one sentence with no field attached, and
      // dropping that would leave a dialog that refused silently.
      setFieldError(failed.fields?.children ?? '')
      setFailure(failed.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add parts inside {part.name || part.id}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {!permission.allowed && <Alert severity="info">{permission.reason}</Alert>}
          {failure && <Alert severity="error">{failure}</Alert>}

          <Typography variant="body2" color="text.secondary">
            A usage of each is declared inside{' '}
            <Typography component="span" variant="body2" sx={{ fontFamily: MONOSPACE }}>
              {part.id}
            </Typography>
            , so every usage of this definition gains it. All or nothing: one refused
            row adds none of them.
          </Typography>

          <ChildPartsTable
            options={options}
            rows={rows}
            onChange={setRows}
            disabled={!permission.allowed || busy}
            error={fieldError}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Tooltip title={refusal} describeChild>
          <span>
            <Button
              variant="contained"
              onClick={() => void submit()}
              disabled={Boolean(refusal) || busy}
              startIcon={busy ? <CircularProgress size={16} /> : undefined}
            >
              {busy ? 'Adding…' : 'Add parts'}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  )
}
